import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  Injector,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild,
} from "@angular/core";
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms";
import { BigNumber } from "shared/cubiq-model-multi-asset";
import { LoggerService } from "@bfx/logger";
import {
  DEFAULT_CHANGE_CALLBACK,
  DEFAULT_TOUCH_CALLBACK,
  OnChangeCallbackType,
  OnTouchCallbackType,
} from "shared/base-components/models/ControlValueAccessorTypes";
import { BfxAmountHelper } from "shared/helper-services/bfx-amount-helper";
import { BfxInputControlStateEnum as EState } from "./models/base-components.enums";
import { SpinDirection, SpinnerButtonsComponent } from "./spinner-buttons.component";
import { BfxInputDirective } from "./bfx-input";
import { BfxFieldNumberDirective } from "./bfx-field-number";
import { EmphasisMixin, EmphasisMixinInputs } from "./mixins/emphasis";
import { ComponentMixin } from "./mixins/mixin";
import { DisabledMixin, DisabledMixinInputs } from "./mixins/disabled";
import { FieldStateMixin, FieldStateMixinInputs } from "./mixins/field-state";
import { SizeMixin, SizeMixinInputs } from "./mixins/size";
import { RequiredMixin, RequiredMixinInputs } from "./mixins/required";
import { InputLabelStateMixin, InputLabelStateMixinInputs } from "./mixins/label-state";
import { InputLabelMixin, InputLabelMixinInputs } from "./mixins/input-label";
import { InputtedMixin, InputtedMixinInputs } from "./mixins/inputted";
import { FieldSuffixMixin, FieldSuffixMixinInputs } from "./mixins/field-suffix";

export const AMOUNT_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => NumberSpinnerComponent),
  multi: true,
};

export const NUMBER_SPINNER_VIEW_PROVIDERS = [BfxInputDirective, BfxFieldNumberDirective];

/**
 * The type of Spin to use - this will determine tth mechanism used to increase/decrease values
 */
export enum SpinType {
  /** Values increment using the defined step value */
  CONSTANT = "CONSTANT",
  /** Values increment based on scale on number rounding on first spin */
  RELATIVE = "RELATIVE",
  /** Values increment based on pipsize, rounding on first spin */
  PIP = "PIP",
  /** Step value provided by an externalStepHandler  */
  EXTERNAL = "EXTERNAL",
}

/** Determines behaviours when updating a value */
export enum UpdateSourceType {
  /** User explicitly loading a value */
  API_LOAD_VALUE,
  /** UI input from user */
  USER_INPUT,
  /** Losing focus */
  BLUR,
  /** Forms API updating a value */
  API_WRITE_VALUE,
  /** Potential value change duer to config change */
  CONFIG_CHANGE,
  /** Spinner API change */
  USER_API,
}

export enum ModelValueType {
  /** Model emits a BigNumbewr value */
  BIG_NUMBER = "BIG_NUMBER",
  /** Model emits a 'number' value */
  NUMBER = "NUMBER",
  /** Model emits a numeric text value */
  STRING = "STRING",
}

/** Specify when model updates are output on the model change emitter */
export enum UpdateMode {
  /** Default - changes are emitted as they occur */
  ONCHANGE = "ONCHANGE",
  /** Changes are only emitted whne docus is lost - useful if lot of processing done on each change */
  ONBLUR = "ONBLUR",
}

export type ExternalStateEmitter = EventEmitter<EState>;
export const ZERO: BigNumber = new BigNumber(0);

/**
 * Type returned detailing the impact of a a key press on a current
 * input field
 */
type AppliedKeyDetails = {
  /** Text in control before input processed */
  initText: string;
  /** Location of decimal pt in inital text */
  decimalPointLocation: number;
  /** New text that has the input applied */
  newText: string;
  /** Count of integer digits in new text */
  newIntCount: number;
  /** Count of decimal digits in new text */
  newDecCount: number;
  /** Number of decimal point chars in new text */
  decCharCount: number;
  /** Location of last minus char in new text */
  lastMinusLocation: number;
};

/** Handle a spin operation on a BigNumber */
export type StepValueProvider = (currentValue: BigNumber, direction: SpinDirection) => BigNumber;

const NEGATIVE_PREFIX = "-";
const PATTERN_KEY: RegExp = /[0-9]/;
const DECIMAL_SEPARATORS: string[] = [".", ","];

const UP_ARROW = "ArrowUp";
const DOWN_ARROW = "ArrowDown";
const ENTER = "Enter";

const logger = LoggerService.for(__dirname);

/**
 * Internal configuration for spinner - generally populated from input properties so
 * we can ensure that all config values are consistent
 */
class SpinnerConfig {
  minPrecision: number = 0;
  maxPrecision: number = 0;
  maxDigits: number = 15;
  step: BigNumber = ZERO;
  allowsNegative: boolean = false;
  hasMin: boolean = false;
  minValue: BigNumber;
  hasMax: boolean = false;
  maxValue: BigNumber;
  minMaxTouchValid: boolean = true;
  emptyValue: string = "";
  createStepValue: StepValueProvider;
  pipSize: number = 0;
  zeroPrefixDecimal: boolean = true;
  keyboardMultipliersEnabled: boolean = false;
  defaultValue: BigNumber = ZERO;
  pressNHold: {
    keyDelay: number;
    keyEventLock: boolean;
  } = { keyDelay: 100, keyEventLock: false };
}

const LOG_DEBUG = false;

const Mixin = SizeMixin(
  InputtedMixin(
    InputLabelStateMixin(
      InputLabelMixin(
        FieldSuffixMixin(
          FieldStateMixin(EmphasisMixin(RequiredMixin(DisabledMixin(ComponentMixin)))),
        ),
      ),
    ),
  ),
);
export const NUMBER_SPINNER_INPUTS = [
  ...SizeMixinInputs,
  ...FieldStateMixinInputs,
  ...FieldSuffixMixinInputs,
  ...DisabledMixinInputs,
  ...EmphasisMixinInputs,
  ...RequiredMixinInputs,
  ...FieldStateMixinInputs,
  ...InputLabelStateMixinInputs,
  ...InputLabelMixinInputs,
  ...InputtedMixinInputs,
];
/**
 * Spinner component for handling numeric input and able to increment/decrement using spin method, arrow keys, or numeric input.
 * Will work well with a Spinner-buttons component that emits spin events.
 *
 */
@Component({
  selector: "number-spinner",
  templateUrl: "./html/number-spinner.html",
  host: {
    ["class.bfx-number-input"]: "true",
  },
  providers: [AMOUNT_VALUE_ACCESSOR],
  viewProviders: NUMBER_SPINNER_VIEW_PROVIDERS,
  inputs: NUMBER_SPINNER_INPUTS,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class NumberSpinnerComponent
  extends Mixin
  implements OnInit, AfterViewInit, OnChanges, ControlValueAccessor
{
  /** Input state properties - input setters are below */
  @Input() inputId: string;

  // @Input() disabled?: boolean = false;

  /** Show the underline bar in the view */
  @Input() showValidationBar?: boolean = true;

  @Input() label: string;

  /** IF true, then the value wil be populated with the default of empty value if left blank */
  @Input() mandatory: boolean = false;

  @Input() min: number | BigNumber = 0;

  @Input() max: number | BigNumber;

  /** Optional step value - default to '1' for CONSTANT but each step type can use different approach */
  @Input() step: number | BigNumber = 1;

  /** String value displayed when the number value is null */
  @Input() emptyValue: string = "";

  /** The least number of decimals to display */
  @Input() minPrecision: number = 0;

  /** THe max number of decimals to display */
  @Input() maxPrecision: number = 0;

  @Input() locked: boolean = false;

  @Input() spinType: SpinType = SpinType.CONSTANT;

  @Input() pipSize: number = 0;

  @Input() tabindex: number = null;

  /** When pushing out model, changes, this specifies the format of the value to push */
  @Input() modelValueType: ModelValueType = ModelValueType.BIG_NUMBER;

  @Input() keyboardMultipliersEnabled: boolean = false;

  /** All key presses will automatically initialise the field if blank, as appropriate, when this is set */
  @Input() keyupAssignZero: boolean = false;

  /** The extimated max number of actual visible characters - if actual number exceed this, then the value will be tooltip */
  @Input() maxVisibleChars: number = 0;

  /** When should changes be propagated */
  @Input() updateMode = UpdateMode.ONCHANGE;

  /** When input has focus, should number be edited as pure number (as opposed to formatted with separators)?  */
  @Input() numericOnInput: boolean = true;

  /** Should mouse wheel be used to spin values when the component has focus? */
  @Input() mouseWheelSpinEnabled = false;

  /** Should non-decimal numbers be grouped using the comma separator */
  @Input() groupNonDecimals: boolean = true;

  /** Flag indiciating whether value that touches min/max bounds is valid */
  @Input() minMaxTouchValid: boolean = true;

  /** External Validation state - set using @input externalState*/
  protected _externalState: EState;

  @Output() blur: EventEmitter<any> = new EventEmitter();

  @Output() change: EventEmitter<any> = new EventEmitter();

  @Output() focus: EventEmitter<any> = new EventEmitter();

  @Output() stateChange: ExternalStateEmitter = new EventEmitter();

  @Output() spinChange: EventEmitter<SpinDirection> = new EventEmitter();

  @Input() inputStartContainer: TemplateRef<Element> = null;
  @Input() inputEndContainer: TemplateRef<Element> = null;
  @Input() fieldStartContainer: TemplateRef<Element> = null;
  @Input() fieldContainer: TemplateRef<Element> = null;
  @Input() fieldEndContainer: TemplateRef<Element> = null;

  @Input() public hideField: boolean = false;

  /***************************************/

  /** CSS classes to apply to the input component - manually updated where dependencies are changed */
  public inputClasses: string[] = [];
  /** CSS classes to apply to the label component - manually updated where dependencies are changed*/
  public labelClasses: string[] = [];

  /* DATA */

  /** Internal configuration derived from public properties */
  protected config: SpinnerConfig;

  /** ControlValueAccessor callback */
  public onModelChange: OnChangeCallbackType = DEFAULT_CHANGE_CALLBACK;

  /** ControlValueAccessor callback */
  public onModelTouched: OnTouchCallbackType = DEFAULT_TOUCH_CALLBACK;

  private internalErrorMsg: string = null;

  // Shows a tooltip if populated
  public toolTip: string = null;

  /** Has the Input element got focus - if so, then need to worry about formatting */
  protected inputFocused: boolean = false;
  /** HAS anyo of the component got focus - i.e. input oir spin buttons */
  protected componentFocused: boolean = false;

  /** The state of control used in ngClass styles - one of INACTIVE, ACTIVE, VALID, INVALID   */
  public state: EState = EState.INACTIVE;

  @ViewChild("inputField") inputField: ElementRef;

  @ViewChild("spinnerButtons") spinnerButtons: SpinnerButtonsComponent;

  /**Is our value equal to the maximum value? */
  public atMax: boolean = false;

  /**Is our value equal to the minimum value? */
  public atMin: boolean = false;

  private logDebug: boolean = false;

  /** Define a an external handler for generating step values */
  public externalStepHandler: StepValueProvider;

  protected data: { value: BigNumber; textValue: string } = { value: null, textValue: null };

  /** Has this component been touched - used for validity states */
  protected hasBeenTouched: boolean = false;

  protected cd: ChangeDetectorRef;
  componentHover: boolean;

  /**
   * Initialise
   */
  constructor(
    public injector: Injector,
    public inputDirective: BfxInputDirective,
    public fieldDirective: BfxFieldNumberDirective,
  ) {
    super(injector);
    this.cd = injector.get(ChangeDetectorRef);
    this.emphasis = "bold";
    this.required = false;
    this.inputted = false;
    this.fieldState = "enabled";

    this.config = new SpinnerConfig();
  }

  ngOnInit(): void {
    this.updateConfiguration();

    this.loadValue(this.data.value);
    this.updateTextValue();
    this.updateStyleClasses();
  }

  /**
   * After any external change, ensure that the internal configuration is consistent4
   */
  protected updateConfiguration(): void {
    if (typeof this.minPrecision === "string") {
      this.min = this.createValueFromText(this.minPrecision);
    }
    if (typeof this.maxPrecision === "string") {
      this.min = this.createValueFromText(this.maxPrecision);
    }
    let minPrecision = this.checkPrecision(this.minPrecision);
    let maxPrecision = this.checkPrecision(this.maxPrecision);
    if (minPrecision > maxPrecision) {
      minPrecision = maxPrecision;
    }

    this.config.zeroPrefixDecimal = true;

    this.config.minPrecision = minPrecision;
    this.config.maxPrecision = maxPrecision;

    this.config.step = new BigNumber(this.step);
    if (this.spinType == SpinType.CONSTANT) {
      // Default step to 1 if not found
      if (this.config.step.isNaN()) {
        this.config.step = new BigNumber(1);
      }
    }

    if (typeof this.min === "string") {
      this.min = this.createValueFromText(this.min);
    }
    if (typeof this.max === "string") {
      this.max = this.createValueFromText(this.max);
    }
    this.config.hasMin =
      typeof this.min === "number" ? !isNaN(this.min) : this.min instanceof BigNumber;
    this.config.minValue = this.getPrecisionMin();
    this.config.allowsNegative = !this.config.hasMin || this.config.minValue.isNegative();

    this.config.hasMax = typeof this.max === "number" ? !isNaN(this.max) : this.max != null;
    this.config.maxValue = this.getPrecisionMax();

    this.config.maxDigits = this.config.maxValue.sd(true);
    this.config.minMaxTouchValid = this.minMaxTouchValid;

    this.config.emptyValue = this.emptyValue;

    if (this.pipSize > 0) {
      this.config.pipSize = this.pipSize;
      this.spinType = SpinType.PIP;
    } else if (this.spinType == null) {
      this.spinType = SpinType.CONSTANT;
    }
    this.config.createStepValue = this.createSpinHandler(this.spinType);

    this.config.keyboardMultipliersEnabled = this.keyboardMultipliersEnabled;
    this.config.pressNHold = {
      keyDelay: 50,
      keyEventLock: false,
    };

    if (this.mandatory && this.config.defaultValue == null) {
      this.setDefaultValue(this.getDefaultValue());
    }
    this.cd.markForCheck();
  }

  protected getDefaultValue(): BigNumber {
    if (this.config.defaultValue) {
      return this.config.defaultValue;
    }

    if (
      this.config.minValue.isLessThanOrEqualTo(ZERO) &&
      this.config.maxValue.isGreaterThanOrEqualTo(ZERO)
    ) {
      return ZERO;
    }
    if (this.config.hasMin) {
      return this.config.minValue;
    }
    return this.config.maxValue;
  }

  /**
   * THe min value may have gretaer precision that our spinner so need to round up
   * @returns
   */
  protected getPrecisionMin(): BigNumber {
    let retMin: BigNumber;

    if (this.config.hasMin) {
      const baseMin = new BigNumber(this.min);
      if (baseMin.decimalPlaces() > this.config.maxPrecision) {
        // Round up to current precision - RoundingMode.ROUND_UP
        retMin = baseMin.decimalPlaces(this.config.maxPrecision, BigNumber.ROUND_UP);
      } else retMin = baseMin;
    } else {
      retMin = new BigNumber(-9e15);
    }
    return retMin;
  }

  /**
   * The max value may have greater precision that our spinner so need to round down
   * @returns
   */
  protected getPrecisionMax(): BigNumber {
    let retMin: BigNumber;

    if (this.config.hasMax) {
      const baseMax = new BigNumber(this.max);
      if (baseMax.decimalPlaces() > this.config.maxPrecision) {
        // Round down to current precision - RoundingMode.ROUND_DOWN
        retMin = baseMax.decimalPlaces(this.config.maxPrecision, 1);
      } else retMin = baseMax;
    } else {
      retMin = new BigNumber(9e15);
    }
    return retMin;
  }

  /**
   * Validate some sensible precision values
   * @param value
   * @returns
   */
  protected checkPrecision(value: number): number {
    if (value == null || isNaN(value)) {
      value = 0;
    } else if (value < 0) {
      value = 0;
    } else if (value > 13) {
      value = 13;
    }
    return value;
  }

  ngOnChanges(changes: SimpleChanges): void {
    let validate = false;
    let restyle = false;
    for (const propName in changes) {
      switch (propName) {
        case "min":
        case "max":
        case "minPrecision":
        case "maxPrecision":
        case "spinType":
        case "step":
        case "maxDigits":
        case "minMaxTouchValid":
        case "mandatory":
          validate = true;
          break;
        case "showValidationBar":
        case "locked":
        case "disabled":
          restyle = true;
          break;
        default:
          break;
      }
    }
    if (validate) {
      if (LOG_DEBUG) {
        logger.out.info("updating config after change");
      }
      // Config inputs have changed so we update and reconfigure
      this.updateConfiguration();
      this.updateValue(this.data.value, UpdateSourceType.CONFIG_CHANGE);
      this.formatInputValue();
    }

    if (restyle) {
      // If any inputs have changed that update the style, then we recalc the classes
      this.updateStyleClasses();
    }
  }

  /**
   * Enable access to all DOM elements
   */
  ngAfterViewInit(): void {
    this.formatInputValue();
  }

  /**********************************************************************/
  /* INITIALIZATION */

  /**********************************************************************/
  /* METHODS */

  public loadValue(initValue: any): void {
    let value: BigNumber = this.createBigNumberFromAny(initValue);
    this.updateValue(value, UpdateSourceType.API_LOAD_VALUE);
    this.formatInputValue();
  }

  public setDefaultValue(defValue: any): void {
    let value: BigNumber = this.createBigNumberFromAny(defValue);
    this.config.defaultValue = value;
    if (this.data.value == null && this.hasBeenTouched) {
      this.updateValue(value, UpdateSourceType.CONFIG_CHANGE);
      this.formatInputValue();
    }
  }

  /**
   * Convert the input into a BigNumber
   * @param initValue  Supports BigNumber, number, string
   * @returns BigNumber or null if input is null or unknown
   */
  protected createBigNumberFromAny(initValue: any): BigNumber {
    let retValue: BigNumber;
    if (initValue instanceof BigNumber) {
      retValue = initValue;
    } else if (initValue == null || initValue === "") {
      retValue = null;
    } else if (typeof initValue === "number") {
      retValue = new BigNumber(initValue);
    } else if (typeof initValue == "string") {
      retValue = this.createValueFromText(initValue);
    }
    return retValue;
  }

  public resetInputControl(): void {
    this.data.value = null;
    this.data.textValue = this.emptyValue;
    if (this.inputField?.nativeElement) {
      this.inputField.nativeElement.value = this.emptyValue;
    }

    this.componentFocused = false;
    this.inputFocused = false;
    this.hasBeenTouched = false;

    this.changeState(EState.INACTIVE);
    this.externalState = null;
    this.onModelChange(this.getModelValue());
    this.setControlValue();
  }

  public lockInputControl(): void {
    this.locked = true;
    this.updateStyleClasses();
  }

  public unlockInputControl(): void {
    this.locked = false;
    this.updateStyleClasses();
  }

  /**
   * Increment the spinner
   */
  public spinUp(): void {
    this.spin(SpinDirection.UP);
    this.updateValidState();
  }

  /**
   * decrement the spinner
   */
  public spinDown(): void {
    this.spin(SpinDirection.DOWN);
    this.updateValidState();
  }

  /**
   * Change value in specified direction, taking the step value from the createStepVAlue fn.
   * @param direction
   */
  public spin(direction: SpinDirection): void {
    this.internalSpin(direction);
  }

  /**
   * Change value in specified direction, from the spin button output
   * @param direction
   */
  public processButtonSpin(direction: SpinDirection): void {
    // If we don't have the spinner buttons marked as active then
    // lets manually set the focus state
    if (!this.componentFocused || this.inputFocused) {
      this.inputFocused = false;
      if (!this.componentFocused) {
        this.setComponentFocused(true);
        this.focus.emit(null);
      }
      // In case any editors have focus we need to wait for these to
      // switch out eg incr slippage on detail fill when market spot has focus
      setTimeout(() => this.internalSpin(direction), 0);
    } else {
      this.internalSpin(direction);
    }
  }

  /**
   * Spin up or down taking the step value from the config. createStepValue function
   * @param direction
   */
  protected internalSpin(direction: SpinDirection): void {
    if (this.config.createStepValue != null) {
      let step: BigNumber = this.config.createStepValue(this.data.value, direction);

      let newValue: BigNumber = this.data.value ? this.data.value.plus(step) : step;
      this.updateValue(newValue, UpdateSourceType.USER_API);
      this.formatInputValue();
      this.spinChange.emit(direction);
    }
  }

  /**
   * Update the value in the model - if the value is not coming from an external source then it will be constrained
   * here between any defined min/max values. When external a value exceeding bounds will be displayed but as invalid
   * @param newValue
   * @param canExceedBounds
   * @returns true if value has been updated, false if value is unchanged
   */
  protected updateValue(newValue: BigNumber, updateSource: UpdateSourceType): boolean {
    if (LOG_DEBUG) {
      logger.out.info(`Updating from ${this.data.value} to ${newValue}`);
    }

    const canExceedBounds =
      updateSource == UpdateSourceType.API_WRITE_VALUE ||
      updateSource == UpdateSourceType.API_LOAD_VALUE ||
      updateSource == UpdateSourceType.BLUR ||
      updateSource == UpdateSourceType.CONFIG_CHANGE;
    const notifyChanges = updateSource != UpdateSourceType.API_WRITE_VALUE;

    // First see if we need to set value to something - if so, it'll probably be the min
    if (
      newValue == null &&
      !canExceedBounds &&
      this.mandatory &&
      this.config.defaultValue &&
      this.hasBeenTouched
    ) {
      newValue = this.config.defaultValue;
    }

    this.atMax = this.config.hasMax && newValue?.isGreaterThanOrEqualTo(this.config.maxValue);
    if (this.atMax && !canExceedBounds) {
      newValue = this.config.maxValue;
    }
    this.atMin = this.config.hasMin && newValue?.isLessThanOrEqualTo(this.config.minValue);
    if (this.atMin && !canExceedBounds) {
      newValue = this.config.minValue;
    }
    const unchanged =
      (newValue == null && this.data.value == null) ||
      (newValue != null && this.data.value != null && newValue.isEqualTo(this.data.value));
    if (!unchanged) {
      this.data.value = newValue;
      this.inputted = true;
      this.updateTextValue();
      this.setControlValue();
      if (notifyChanges) {
        this.onChange();
      }
      this.cd.markForCheck();
    }
    return !unchanged;
  }

  /**
   * Process focus event from buttons
   */
  public processButtonFocus(): void {
    if (LOG_DEBUG) {
      logger.out.info("Button Focus ");
    }
    if (!this.componentFocused) {
      this.setComponentFocused(true);
      // this.focusInput();

      this.setControlValue();
      this.formatInputValue();
      this.updateValidStateAsync();
      this.focus.emit(null);
    }
  }

  /**
   * Ensure the text input has focus
   */
  public focusInput() {
    if (!this.componentFocused) {
      this.setComponentFocused(true);
      setTimeout(() => this.inputField.nativeElement.focus(), 50);
    }
  }

  /**
   * Ensure values are formatted correctly for input and input/validation states are correct.
   * The input selection text is schedule for update
   * @param event
   */
  public onFocus(event: any): void {
    if (LOG_DEBUG) {
      logger.out.info("On Focus");
    }
    this.inputFocused = true;
    this.setComponentFocused(true);

    this.setControlValue();
    this.formatInputValue();
    this.toolTip = null;
    this.focus.emit(event);

    const element = event?.target;

    if (element instanceof HTMLInputElement) {
      setTimeout(() => {
        if (element.selectionStart == element.selectionEnd) {
          this.highlightOnFocus(element);
        }
      }, 100);
    }
  }

  /**
   * When input receives focus highlight the contents - this needs to be in setTImeout() as a mouse
   * click will override the selected text - 100ms seems empirically fine.
   * Can be overridden to highlight only pips part of number etc
   */
  protected highlightOnFocus(element: HTMLInputElement): void {
    element.select();
  }

  /**
   * Process the removal of focus from the spinner buttons
   */
  public processButtonBlur(event: FocusEvent): void {
    if (LOG_DEBUG) {
      logger.out.info("Button blur");
    }
    if (event.relatedTarget == this.inputField?.nativeElement) {
      // Focus is just passing from spinner buttons to input
      return;
    }
    this.setComponentFocused(false);
    this.setControlValue();
    this.formatInputValue();
    this.updateValidStateAsync();
    if (this.updateMode == UpdateMode.ONBLUR) {
      this.onChange();
    }
    this.blur.emit(null);
  }

  /**
   * Process removal of focus from component - as focus is gone, we may emit
   * a change event if required, and reformat the displayed text
   * @param event
   */
  public onBlur(event: FocusEvent): void {
    if (LOG_DEBUG) {
      logger.out.info("on blur");
    }
    this.inputFocused = false;
    if (this.spinnerButtons != null && this.spinnerButtons.isFocusTarget(event)) {
      return;
    }
    this.setComponentFocused(false);
    this.onModelTouched();
    this.hasBeenTouched = true;

    // When we tab out, empty value can be updated with default value
    if (this.data.value == null && this.config.defaultValue != null) {
      this.updateValue(null, UpdateSourceType.BLUR);
    }
    this.setControlValue();
    this.formatInputValue();
    // this.onChange(null);
    this.updateValidState();
    if (this.updateMode == UpdateMode.ONBLUR) {
      this.onChange();
    }
    this.blur.emit(event);
  }

  /**
   * Check whether the string value should be set as tooltip
   * @param strValue
   */
  public tooltipVisibility(strValue: string): void {
    if (this.maxVisibleChars) {
      if (strValue && strValue.length >= this.maxVisibleChars) {
        this.toolTip = strValue;
      } else {
        this.toolTip = null;
      }
    }
  }

  /**
   * Update the internal inputFocused property - changes to this also propagate to
   * validation style classes
   * @param value
   */
  protected setComponentFocused(value: boolean) {
    if (this.componentFocused != value) {
      this.componentFocused = value;
      this.updateStyleClasses();
    }
  }

  /**
   * Intercept the paste and run it through the same processing loop as stardard keyboard
   * input
   * @param event
   */
  public onPaste(event: ClipboardEvent): void {
    if (event && event.clipboardData) {
      const input = event.clipboardData.getData("text");

      const element: HTMLInputElement = this.inputField.nativeElement;
      let selIndex = element.selectionStart;
      let selEndIndex = element.selectionEnd;
      for (let i = 0; input && i < input.length; i++) {
        const inputChar = input.substring(i, i + 1);
        if (inputChar != "," && this.processStringInput(inputChar)) {
          // Char is good so let's update the display text
          element.value =
            element.value.substring(0, selIndex) + inputChar + element.value.substring(selEndIndex);
          selIndex++;
          selEndIndex = selIndex;
          element.setSelectionRange(selIndex, selIndex);
        } else {
          // the input might have been updated due to decimal point so let's recheck selection
          selIndex = element.selectionStart;
          selEndIndex = element.selectionEnd;
        }
        this.updateValueFromInputText(element.value);
      }
      event.preventDefault();
    }
  }

  /**
   * convert the string argument to a BigNumber and update the model
   * @param inputValue
   */
  protected updateValueFromInputText(inputValue: string): void {
    if (LOG_DEBUG) {
      logger.out.info("strValue = " + inputValue);
    }
    let numValue = this.createValueFromText(inputValue);
    if (numValue && numValue.isNaN()) {
      numValue = null;
    }
    this.updateValue(numValue, UpdateSourceType.USER_INPUT);
  }

  /**
   * Convert string representation to normalised BigNumber or null if no number found.
   * "-", "-." and "." all map to zero
   * @param strValue
   * @returns
   */
  protected createValueFromText(strValue: string): BigNumber {
    let retNumber = null;
    const strippedValue = strValue ? strValue.replace(/,/g, "") : "";

    if (!strippedValue) {
      retNumber = null;
    } else if (strippedValue == "." || strippedValue == "-" || strippedValue == "-.") {
      retNumber = new BigNumber(0);
    } else {
      try {
        retNumber = new BigNumber(strippedValue);
        if (retNumber.isZero() && retNumber.isNegative()) {
          // Change -0 to 0
          retNumber = new BigNumber(0);
        }
      } catch (e) {
        logger.out.warn(`Cannot parse ${strippedValue} as number`);
      }
    }
    return retNumber;
  }

  /**
   * Handle a change to the text in the input. This is where keyboard input is processed

   * @param event 
   */
  public onInputChange(event: InputEvent): void {
    const strValue = (event.target as HTMLInputElement).value;

    this.updateValueFromInputText(strValue);
    this.setControlValue();
    this.onModelTouched();
    this.onChange();

    this.change.emit(strValue);
  }

  /**
   * Called when auto writing a value to this view component from an external model
   * @param value
   */
  writeValue(value): void {
    if (LOG_DEBUG) {
      logger.out.info(`Writing value ${value}`);
    }
    let newValue: BigNumber = this.createBigNumberFromAny(value);
    if (this.updateValue(newValue, UpdateSourceType.API_WRITE_VALUE) && !this.inputFocused) {
      // We can get the same value being pushed back in as we've notifiied and
      // we don't want to update formatting e.g. if min precision is set then 1 becomes 1.00
      this.formatInputValue();
    }
  }

  /**
   * ControlValueAccessor interface method
   * @param fn
   */
  public registerOnChange(fn: OnChangeCallbackType): void {
    this.onModelChange = fn;
  }

  /**
   * ControlValueAccessor interface method
   * @param fn
   */
  public registerOnTouched(fn: OnTouchCallbackType): void {
    this.onModelTouched = fn;
  }

  /**
   * Return an approriate model value to emit on the onModelChange emitter. THis uses the
   * modelValueType to determine the correct representation.
   *
   * @returns
   */
  protected getModelValue(): any {
    switch (this.modelValueType) {
      case ModelValueType.BIG_NUMBER:
        return this.data.value;
      case ModelValueType.STRING:
        return this.data.textValue;
      case ModelValueType.NUMBER:
      default:
        return this.numberValue;
    }
  }

  /**
   * Check to ensure value is within bound and recalculate valid state - schedules if reqd
   */
  protected setControlValue() {
    this.checkControlValue();
  }

  /**
   * Check to ensure value is within bound and recalculate valid state
   */
  private checkControlValue(): void {
    this.atMax =
      this.data.value != null &&
      this.config.hasMax &&
      this.data.value.isGreaterThanOrEqualTo(this.config.maxValue);
    this.atMin =
      this.data.value != null &&
      this.config.hasMin &&
      this.data.value.isLessThanOrEqualTo(this.config.minValue);

    this.updateValidState();
  }

  /**
   * Convert the model number value to text and update the input
   */
  protected formatInputValue(): void {
    const strValue = this.formatNumber(this.data.value, this.inputFocused);
    if (LOG_DEBUG) {
      logger.out.info("Setting input text to " + strValue);
    }
    if (this.inputField?.nativeElement) {
      this.inputField.nativeElement.value = strValue;
    }
    this.tooltipVisibility(strValue);
  }

  /**
   * Covert the model value to text for display - styles are updated as label visibility
   * may change
   */
  protected updateTextValue(): void {
    const strValue = this.formatNumber(this.data.value, false);
    if (LOG_DEBUG) {
      logger.out.info("Setting text value to " + strValue);
    }
    this.data.textValue = strValue;
    this.updateStyleClasses();
  }

  protected formatNumber(value: BigNumber, formatAsFocused: boolean): string {
    let isValidNumber: boolean = value != null && !value.isNaN();
    if (isValidNumber) {
      const minPrecision = this.config.minPrecision;
      const maxPrecision = this.config.maxPrecision;

      let negStr = "";
      if (value && value.isNegative()) {
        // We can't get the digits for -0.x - intValue will be -0 which becomes 0
        value = value.negated();
        negStr = "-";
      }
      const strValue = value.toFixed(maxPrecision);

      let decIndex = strValue.indexOf(".");
      if (decIndex == -1) {
        decIndex = strValue.length;
      }

      const intPart = strValue.substring(0, decIndex);
      const useGrouping = this.groupNonDecimals && (!this.numericOnInput || !formatAsFocused);
      // DEcimalPipe coverts all strings to numbers to can't handle anything bigger than 1e14 without rounding
      // So add comma grouping (from StackOverflow RegExp https://stackoverflow.com/questions/2901102/how-to-print-a-number-with-commas-as-thousands-separators-in-javascript)

      const intFormatted = useGrouping ? intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",") : intPart;

      let decFormatted: string = "";
      if (maxPrecision > 0 && decIndex >= 0) {
        decFormatted = strValue.substring(decIndex + 1);
        while (decFormatted.length > minPrecision && decFormatted.endsWith("0")) {
          decFormatted = decFormatted.substring(0, decFormatted.length - 1);
        }
        if (decFormatted) {
          decFormatted = "." + decFormatted;
        }
      }
      let fullFormatted = negStr + intFormatted + decFormatted;

      return fullFormatted;
    }
    return this.config.emptyValue;
  }

  // public onSelect( event:any){
  //   const selection = event.target.value.substring(event.target.selectionStart, event.target.selectionEnd);
  // logger.out.warn(`You selected: ${selection} from ${event.target.selectionStart}, ${event.target.selectionEnd}`);
  // }

  protected onChange(): void {
    if (LOG_DEBUG) {
      logger.out.info(`Calling model change with "${this.getModelValue()}"`);
    }
    // We only want to emit a change if the component is configured to update on every change
    // or if the component no longer has focus
    if (this.updateMode == UpdateMode.ONCHANGE || !this.componentFocused) {
      this.onModelChange(this.getModelValue());
      this.change.emit(this.data.value);
    }
  }

  public onMouseWheel(event: WheelEvent): void {
    if (!this.mouseWheelSpinEnabled || !this.componentFocused) {
      return;
    }
    let direction: SpinDirection = event.deltaY > 0 ? SpinDirection.DOWN : SpinDirection.UP;
    this.spin(direction);
  }

  public onKeyDown(event: KeyboardEvent): void {
    if (!this.config.pressNHold) {
      // Somehow we're misconfigured
      return;
    }
    if (event.key == UP_ARROW || event.key == DOWN_ARROW) {
      event.preventDefault();
      if (!this.config.pressNHold.keyEventLock) {
        this.config.pressNHold.keyEventLock = true;
        setTimeout(() => {
          this.config.pressNHold.keyEventLock = false;
          if (event.key == UP_ARROW) {
            this.spinUp();
          } else if (event.key == DOWN_ARROW) {
            this.spinDown();
          }
        }, this.config.pressNHold.keyDelay);
      }
    } else if (event.key == ENTER && this.updateMode == UpdateMode.ONBLUR) {
      event.preventDefault();
      this.onModelChange(this.getModelValue());
    } else if (this.applyKeyEventToAmount(event)) {
      event.preventDefault();
    }
    // Clear the tooltip as this will not match the displayed text until formatValue() called
    this.toolTip = null;
  }

  /**
   * Ensure any key press on blank field initialises if flag is set
   */
  public onKeyUp(_event: KeyboardEvent): void {
    try {
      if (this.inputField.nativeElement.value.length == 0 && this.keyupAssignZero) {
        const initValue = this.getDefaultValue();
        this.updateValue(initValue, UpdateSourceType.USER_INPUT);
      }
    } catch (event) {}
  }

  onPointerOver(_) {
    this.componentHover = true;
    this.updateInputClasses();
    this.cd.markForCheck();
  }

  onPointerOut(_) {
    this.componentHover = false;
    this.updateValidState();
    this.updateInputClasses();
    this.cd.markForCheck();
  }

  /**
   * Capture and apply any special keyboard multipliers if configured.
   *
   * @param event Keyboard Event to process
   * @returns true if key processed
   */
  protected applyKeyEventToAmount(event: KeyboardEvent): boolean {
    if (this.data.value == null || !this.config.keyboardMultipliersEnabled) {
      return false;
    }

    if (event.key && event.key.length == 1) {
      let multiplier: number = BfxAmountHelper.getAmtMultiplierForChar(event.key);
      if (multiplier > 1 && !this.atMax) {
        const currentValue = this.getVisibleCurrentValue();
        const newValue = currentValue.times(multiplier);
        this.updateValue(newValue, UpdateSourceType.USER_INPUT);
        this.formatInputValue();
        return true;
      }
    }
    return false;
  }

  /**
   * When entered values are less than min or greater than max, then the internal value will be the constrained one.
   * However, for scaling, or incr/decr operations we want the internal value to be what is displayed in the input
   * @returns
   */
  protected getVisibleCurrentValue(): BigNumber {
    if (!this.atMin || this.atMax) {
      return this.data.value;
    }
    const existingText = this.inputField?.nativeElement?.value ?? "0";
    return this.createValueFromText(existingText);
  }

  public onKeyPress(event: KeyboardEvent): void {
    if (event.key && event.key.length == 1) {
      // Ignore any non-printable keys
      let inputChar: string = event.key;
      const allGood = this.processStringInput(inputChar);
      if (!allGood) {
        event.preventDefault();
      }
    }
  }

  /**
   * Process a new char coming into input - can be either from a key press or from
   * iterating over a pasted block of text
   * @param inputChar
   * @returns
   */
  protected processStringInput(inputChar: string): boolean {
    let numberEntered: boolean = PATTERN_KEY.test(inputChar);
    let decimalEntered: boolean = DECIMAL_SEPARATORS.indexOf(inputChar) >= 0;

    const allowNegative = this.config.allowsNegative;
    const minusEntered = allowNegative && NEGATIVE_PREFIX === inputChar;

    if (!numberEntered && !decimalEntered && !minusEntered) {
      return false;
    }

    if (!this.inputField?.nativeElement) {
      return false;
    }

    const element: HTMLInputElement = this.inputField.nativeElement;
    if (inputChar == ",") {
      inputChar = ".";
    }

    const appliedKeyDetails: AppliedKeyDetails = this.getAppliedKeyDetails(element, inputChar);
    if (appliedKeyDetails.lastMinusLocation > 0) {
      // Can't have a minus except at position 0
      return false;
    }

    if (appliedKeyDetails.newIntCount > 1) {
      const checkIndex = appliedKeyDetails.lastMinusLocation == 0 ? 1 : 0;
      if (appliedKeyDetails.newText[checkIndex] == "0") {
        // Can't have a  leading '0' or '-0' when more than one int digit
        return false;
      }
    }

    if (this.config.maxPrecision == 0 && appliedKeyDetails.decCharCount > 0) {
      // Decimal points not supported
      return false;
    }

    if (appliedKeyDetails.decCharCount > 1) {
      // Multiple decimal points not supported
      if (
        element.selectionStart == appliedKeyDetails.decimalPointLocation &&
        element.selectionEnd == appliedKeyDetails.decimalPointLocation
      ) {
        // If we type a '.' beside the existing ',' we just move the cursor
        element.setSelectionRange(
          appliedKeyDetails.decimalPointLocation + 1,
          appliedKeyDetails.decimalPointLocation + 1,
        );
      }
      return false;
    }

    if (appliedKeyDetails.newDecCount > this.config.maxPrecision) {
      // Already got a full house of decimals
      return false;
    }

    if (appliedKeyDetails.newIntCount > this.config.maxDigits) {
      // Already got a full house of ints
      return false;
    }

    if (decimalEntered) {
      const firstPos = appliedKeyDetails.lastMinusLocation == 0 ? 1 : 0;

      // If we type a '.' at first pos we should also prefix with '0'
      const selLocn = element.selectionStart;
      const decReplacement = selLocn == firstPos && this.config.zeroPrefixDecimal ? "0." : ".";
      element.value =
        element.value.substring(0, selLocn) +
        decReplacement +
        element.value.substring(element.selectionEnd);
      element.setSelectionRange(selLocn + decReplacement.length, selLocn + decReplacement.length);

      // THese should be elsewhere but now sure how - we normally update the vcalues when inputChange fires
      // but we don't want to process InputEvents when we want to change the input char e.g. from ',' to '.'
      this.updateValueFromInputText(element.value);

      return false;
    }

    return true;
  }

  public updateValidStateAsync(offset: number = 0): void {
    setTimeout(() => {
      this.updateValidState();
    }, offset);
  }

  /**
   * Ensure the state property correctly reflects the value - checks for active, existence, min and max constraints
   */
  public updateValidState(): void {
    if (this.componentFocused) {
      // External state will always get blanked once the component has focus
      this._externalState = null;
    }

    if (this._externalState != null) {
      const state = this._externalState;
      this.changeState(state);
      if (this._externalState !== EState.INVALID) {
        this._externalState = null;
      }
    } else if (this.componentFocused) {
      this.changeState(EState.ACTIVE);
    } else if (this.data.value == null) {
      this.changeState(this.mandatory && this.hasBeenTouched ? EState.INVALID : EState.INACTIVE);
    } else if (
      this.config.minMaxTouchValid &&
      ((this.config.hasMin && this.data.value.isLessThan(this.config.minValue)) ||
        (this.config.hasMax && this.data.value.isGreaterThan(this.config.maxValue)))
    ) {
      this.changeState(EState.INVALID);
    } else if (
      !this.config.minMaxTouchValid &&
      ((this.config.hasMin && this.data.value.isLessThanOrEqualTo(this.config.minValue)) ||
        (this.config.hasMax && this.data.value.isGreaterThanOrEqualTo(this.config.maxValue)))
    ) {
      this.changeState(EState.INVALID);
    } else {
      this.changeState(EState.VALID);
    }
    this.cd.markForCheck();
  }

  /**
   * Update the view state of the component
   * @param state
   */
  public changeState(state: EState) {
    this.state = state;
    this.updateStyleClasses();
    this.stateChange.emit(this.state);
  }

  @Input()
  public get externalState(): EState {
    return this._externalState;
  }

  public set externalState(value: EState) {
    if (this._externalState != value) {
      this._externalState = value;
      this.updateValidState();
    }
  }

  /**
   * Get the spinner value as a number. If the internal BigNumber value is null, then this
   * also returns a null value
   */
  public get numberValue(): number | null {
    if (this.data.value != null) {
      return this.data.value.toNumber();
    }
    return null;
  }

  public get bigNumberValue(): BigNumber {
    return this.data.value;
  }

  public get numericTextValue(): string {
    return this.data.textValue;
  }

  public set precision(value: number) {
    this.minPrecision = value;
    this.maxPrecision = value;
  }
  /**
   * Figure out what the full input value would be after applying the specified input. This make sit easy to see whether
   * an input will result in an invalid number string e.g. can return "-2-3.4" "0.4.5", "0045", ".-5" etc
   * @param inputElement
   * @param input
   * @returns
   */
  protected getAppliedKeyDetails(inputElement: HTMLInputElement, input: string): AppliedKeyDetails {
    const element: HTMLInputElement = inputElement;
    const selectStart = element.selectionStart;
    const selectEnd = element.selectionEnd;

    const initText = element.value;

    // Location of existing decimal point - if not found then implicitly at end of string
    let decimalPointLocation: number = initText.indexOf(".");
    if (decimalPointLocation == -1) {
      decimalPointLocation = initText.length;
    }

    const newText = initText.substring(0, selectStart) + input + initText.substring(selectEnd);
    let newDecLocation = newText.indexOf(".");
    let lastDecLocation = newText.lastIndexOf(".");

    let decCharCount: number;
    if (newDecLocation == -1) {
      decCharCount == 0;
      newDecLocation = newText.length;
    } else if (lastDecLocation !== newDecLocation) {
      decCharCount = 2;
    } else {
      decCharCount = 1;
    }

    let lastMinusLocation = newText.lastIndexOf("-");

    // Integer part with no grouping commas
    const newInstStr = newText.substring(0, newDecLocation).replace(/,/g, "");
    const newIntCount = newInstStr.length - (lastMinusLocation >= 0 ? 1 : 0);
    const newDecCount = newText.length - newDecLocation - 1;

    return {
      initText,
      decimalPointLocation,
      newText,
      newIntCount,
      newDecCount,
      decCharCount,
      lastMinusLocation,
    };
  }

  /**
   * Return step spin value purely based on configured step property
   * @param value
   * @param direction
   * @returns
   */
  protected constantSpinHandler(value: BigNumber, direction: SpinDirection): BigNumber {
    const posStep: BigNumber = new BigNumber(this.config.step);
    if (direction == SpinDirection.UP) {
      return posStep;
    }
    return posStep.negated();
  }

  /**
   * Spin stepper for pricing pips - when partial pips have been specified, the step will be the fraction of pip needed
   * to round up  or donw tro next whole pip value
   * @param value
   * @param direction
   * @returns
   */
  protected pipSpinHandler(value: BigNumber, direction: SpinDirection): BigNumber {
    let step = this.config.step;
    if (value && this.config.pipSize && this.config.pipSize < this.config.maxPrecision) {
      const newValue =
        direction == SpinDirection.UP
          ? value.plus(step).decimalPlaces(this.config.pipSize, BigNumber.ROUND_DOWN)
          : value.minus(step).decimalPlaces(this.config.pipSize, BigNumber.ROUND_UP);
      return newValue.minus(value);
    } else if (direction == SpinDirection.UP) {
      return step;
    }
    return step.negated();
  }

  /**
   * Spin stepper for amounts - round step size up to biggest power of ten
   * @param value
   * @param direction
   * @returns
   */
  protected relativeSpinHandler(_currentvalue: BigNumber, direction: SpinDirection): BigNumber {
    if (!_currentvalue) {
      _currentvalue = this.getDefaultValue();
    }
    const allZeros = _currentvalue.precision(1, BigNumber.ROUND_DOWN).isEqualTo(_currentvalue);
    const isNeg = _currentvalue.isNegative();
    if (isNeg) {
      _currentvalue = _currentvalue.negated();
      direction = direction == SpinDirection.UP ? SpinDirection.DOWN : SpinDirection.UP;
    }

    let retStep: BigNumber;
    if (allZeros) {
      // Adding a 1 and rounding up will take us to the next value we want to get to
      const newValue =
        direction == SpinDirection.UP
          ? _currentvalue.plus(1).precision(1, BigNumber.ROUND_UP)
          : _currentvalue.minus(1).precision(1, BigNumber.ROUND_DOWN);
      retStep = newValue.minus(_currentvalue);
    } else {
      // We are not at exact boundary so no adjustment needed - rounding
      // will take us to the next value we want to get to
      const newValue =
        direction == SpinDirection.UP
          ? _currentvalue.precision(1, BigNumber.ROUND_UP)
          : _currentvalue.precision(1, BigNumber.ROUND_DOWN);
      retStep = newValue.minus(_currentvalue);
    }
    if (isNeg) {
      return retStep.negated();
    }
    return retStep;
  }

  /**
   * Create appropriate handler for creating Step values
   * @param spinType
   * @returns
   */
  protected createSpinHandler(spinType: SpinType): StepValueProvider {
    let retValue: StepValueProvider;
    switch (spinType) {
      case SpinType.CONSTANT:
        retValue = this.constantSpinHandler.bind(this);
        break;
      case SpinType.RELATIVE:
        retValue = this.relativeSpinHandler.bind(this);
        break;
      case SpinType.PIP:
        retValue = this.pipSpinHandler.bind(this);
        break;
      default:
        if (this.externalStepHandler != null) {
          retValue = this.externalStepHandler;
        } else {
          retValue = this.constantSpinHandler.bind(this);
        }
        break;
    }
    return retValue;
  }

  /**
   * Create all style classes programatically.
   */
  protected updateStyleClasses(): void {
    this.updateInputClasses();
    this.updateLabelClasses();
    this.cd.markForCheck();
  }

  /**
   * Define classes to be applied to the input
   */
  protected updateInputClasses(): void {
    const locked = this.locked || this.disabled;

    if (this.componentFocused || this.inputFocused) {
      this.fieldState = "active";
    } else if (this.componentHover) {
      this.fieldState = "hover";
    } else if (this.showValidationBar) {
      if (this.state == EState.VALID && !this.internalErrorMsg && !locked) {
        this.fieldState = "enabled";
      } else if ((this.state == EState.INVALID || this.internalErrorMsg) && !locked) {
        this.fieldState = "invalid";
      } else if (this.state == EState.INACTIVE && !locked) {
        this.fieldState = "disabled";
      } else if (this.state == EState.ACTIVE && !locked) {
        this.fieldState = "active";
      }
    } else {
      this.fieldState = "enabled";
    }
    if (this.locked) {
      this.fieldState = "disabled";
    }
    if (this.disabled) {
      this.fieldState =
        this.state == EState.INVALID || (this.internalErrorMsg && !locked) ? "invalid" : "disabled";
    }
  }

  /**
   * Recalculate the set of classes to apply to the label
   */
  protected updateLabelClasses(): void {
    const locked = this.locked;
    const lockedOrDisabled = this.locked || this.disabled;
    if (this.locked) {
      this.inputLabelState = "disabled";
    } else if (this.showValidationBar) {
      if (this.state == EState.VALID && !this.internalErrorMsg && !lockedOrDisabled) {
        this.inputLabelState = "enabled";
      } else if ((this.state == EState.INVALID || this.internalErrorMsg) && !locked) {
        this.inputLabelState = "invalid";
      } else if (this.state == EState.INACTIVE) {
        this.inputLabelState = "read-only";
      } else if (this.state == EState.ACTIVE && !lockedOrDisabled) {
        this.inputLabelState = "enabled";
      } else {
        this.inputLabelState = "disabled";
      }
    } else {
      this.inputLabelState = "enabled";
    }
  }

  /**
   * bind properties required by MixinDirective change detection
   */
  get bfxInput(): Record<string, any> {
    return this.inputDirective.directiveHost(this);
  }

  /**
   * bind properties required by MixinDirective change detection
   */
  get bfxFieldNumber(): Record<string, any> {
    return this.fieldDirective.directiveHost(this);
  }

  /**
   * Define an external error message - when defined, the control is automatically marked as invalid
   */
  public get errorMsg(): string {
    return this.internalErrorMsg;
  }

  /**
   * Define an external error message - when defined, the control is automatically marked as invalid
   */
  public set errorMsg(value: string) {
    if (value !== this.internalErrorMsg) {
      this.internalErrorMsg = value;
      this.updateStyleClasses();
    }
  }
}
